import React, { useState, useEffect } from 'react';
import { Calendar, X, Download } from 'lucide-react';
import apiService from '../../services/api';

const FiltrosAvanzados = ({ filtros, onChange, onExportar }) => {
  const [filtrosDisponibles, setFiltrosDisponibles] = useState({
    clientes: [],
    navieras: []
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(true);

  useEffect(() => {
    fetchFiltrosDisponibles();
  }, []);

  const fetchFiltrosDisponibles = async () => {
    try {
      const data = await apiService.get('/reportes/filtros-disponibles');
      setFiltrosDisponibles(data);
    } catch (error) {
      console.error('Error al cargar filtros:', error);
    }
  };

  const handleFechaPreset = (preset) => {
    const hoy = new Date();
    let fechaInicio = '';

    switch (preset) {
      case 'mes':
        fechaInicio = new Date(hoy.getFullYear(), hoy.getMonth(), 1).toISOString().split('T')[0];
        break;
      case 'trimestre':
        fechaInicio = new Date(hoy.getFullYear(), hoy.getMonth() - 3, 1).toISOString().split('T')[0];
        break;
      case 'año':
        fechaInicio = new Date(hoy.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      case 'ytd':
        fechaInicio = new Date(hoy.getFullYear(), 0, 1).toISOString().split('T')[0];
        break;
      default:
        fechaInicio = '';
    }

    onChange({
      ...filtros,
      fechaInicio,
      fechaFin: hoy.toISOString().split('T')[0]
    });
  };

  const toggleCliente = (clienteId) => {
    const nuevosClientes = filtros.clientes.includes(clienteId)
      ? filtros.clientes.filter(id => id !== clienteId)
      : [...filtros.clientes, clienteId];
    onChange({ ...filtros, clientes: nuevosClientes });
  };

  const toggleNaviera = (navieraId) => {
    const nuevasNavieras = filtros.navieras.includes(navieraId)
      ? filtros.navieras.filter(id => id !== navieraId)
      : [...filtros.navieras, navieraId];
    onChange({ ...filtros, navieras: nuevasNavieras });
  };

  const toggleAlerta = (alerta) => {
    const nuevasAlertas = filtros.alertas.includes(alerta)
      ? filtros.alertas.filter(a => a !== alerta)
      : [...filtros.alertas, alerta];
    onChange({ ...filtros, alertas: nuevasAlertas });
  };

  const limpiarFiltros = () => {
    onChange({
      fechaInicio: '',
      fechaFin: '',
      clientes: [],
      navieras: [],
      alertas: ['verde', 'amarillo', 'rojo'],
      facturacion: 'todos',
      compararPeriodo: false,
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-soft border border-ink-200 p-6 mb-6 animate-fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-ink-900 flex items-center gap-2">
          <Calendar size={20} />
          Filtros de Reporte
        </h3>
        <button
          onClick={() => setMostrarFiltros(!mostrarFiltros)}
          className="text-sm text-ink-600 hover:text-ink-900"
        >
          {mostrarFiltros ? 'Ocultar' : 'Mostrar'}
        </button>
      </div>

      {mostrarFiltros && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Rango de Fechas */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Período
              </label>
              <div className="flex gap-2 mb-2">
                <button
                  onClick={() => handleFechaPreset('mes')}
                  className="px-2 py-1 text-xs bg-ink-100 hover:bg-ink-200 text-ink-700 rounded"
                >
                  Último mes
                </button>
                <button
                  onClick={() => handleFechaPreset('trimestre')}
                  className="px-2 py-1 text-xs bg-ink-100 hover:bg-ink-200 text-ink-700 rounded"
                >
                  Trimestre
                </button>
                <button
                  onClick={() => handleFechaPreset('año')}
                  className="px-2 py-1 text-xs bg-ink-100 hover:bg-ink-200 text-ink-700 rounded"
                >
                  Año
                </button>
              </div>
              <div className="space-y-2">
                <input
                  type="date"
                  value={filtros.fechaInicio}
                  onChange={(e) => onChange({ ...filtros, fechaInicio: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent text-sm"
                  placeholder="Desde"
                />
                <input
                  type="date"
                  value={filtros.fechaFin}
                  onChange={(e) => onChange({ ...filtros, fechaFin: e.target.value })}
                  className="w-full px-3 py-2 border border-ink-300 rounded-lg focus:ring-2 focus:ring-ink-900 focus:border-transparent text-sm"
                  placeholder="Hasta"
                />
              </div>
            </div>

            {/* Clientes */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Clientes
              </label>
              <div className="max-h-32 overflow-y-auto border border-ink-300 rounded-lg p-2 space-y-1">
                {filtrosDisponibles.clientes.map(cliente => (
                  <label key={cliente.id} className="flex items-center gap-2 hover:bg-ink-50 p-1 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filtros.clientes.includes(cliente.id)}
                      onChange={() => toggleCliente(cliente.id)}
                      className="rounded text-ink-900 focus:ring-ink-900"
                    />
                    <span className="text-sm text-ink-700">{cliente.nombre}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Navieras */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Navieras
              </label>
              <div className="max-h-32 overflow-y-auto border border-ink-300 rounded-lg p-2 space-y-1">
                {filtrosDisponibles.navieras.map(naviera => (
                  <label key={naviera.id} className="flex items-center gap-2 hover:bg-ink-50 p-1 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filtros.navieras.includes(naviera.id)}
                      onChange={() => toggleNaviera(naviera.id)}
                      className="rounded text-ink-900 focus:ring-ink-900"
                    />
                    <span className="text-sm text-ink-700">{naviera.nombre}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Estados de Alerta */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Estados de Alerta
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtros.alertas.includes('verde')}
                    onChange={() => toggleAlerta('verde')}
                    className="rounded text-ink-900 focus:ring-ink-900"
                  />
                  <div className="w-3 h-3 rounded-full bg-green-500"></div>
                  <span className="text-sm text-ink-700">Verde</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtros.alertas.includes('amarillo')}
                    onChange={() => toggleAlerta('amarillo')}
                    className="rounded text-ink-900 focus:ring-ink-900"
                  />
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-ink-700">Amarillo</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filtros.alertas.includes('rojo')}
                    onChange={() => toggleAlerta('rojo')}
                    className="rounded text-ink-900 focus:ring-ink-900"
                  />
                  <div className="w-3 h-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-ink-700">Rojo</span>
                </label>
              </div>
            </div>

            {/* Estado de Facturación */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Facturación
              </label>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={filtros.facturacion === 'todos'}
                    onChange={() => onChange({ ...filtros, facturacion: 'todos' })}
                    className="text-ink-900 focus:ring-ink-900"
                  />
                  <span className="text-sm text-ink-700">Todos</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={filtros.facturacion === 'facturados'}
                    onChange={() => onChange({ ...filtros, facturacion: 'facturados' })}
                    className="text-ink-900 focus:ring-ink-900"
                  />
                  <span className="text-sm text-ink-700">Facturados</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    checked={filtros.facturacion === 'pendientes'}
                    onChange={() => onChange({ ...filtros, facturacion: 'pendientes' })}
                    className="text-ink-900 focus:ring-ink-900"
                  />
                  <span className="text-sm text-ink-700">Pendientes</span>
                </label>
              </div>
            </div>

            {/* Comparar Período */}
            <div>
              <label className="text-sm font-medium text-ink-700 mb-2 block">
                Comparación
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filtros.compararPeriodo}
                  onChange={(e) => onChange({ ...filtros, compararPeriodo: e.target.checked })}
                  className="rounded text-ink-900 focus:ring-ink-900"
                />
                <span className="text-sm text-ink-700">Comparar con período anterior</span>
              </label>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-4 border-t border-ink-200">
            <button
              onClick={limpiarFiltros}
              className="px-4 py-2 border border-ink-300 text-ink-700 rounded-lg hover:bg-ink-50 transition-colors text-sm font-medium"
            >
              Limpiar Filtros
            </button>
            <button
              onClick={onExportar}
              className="ml-auto px-4 py-2 bg-ink-900 text-white rounded-lg hover:bg-ink-800 transition-colors flex items-center gap-2 text-sm font-medium"
            >
              <Download size={16} />
              Exportar
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default FiltrosAvanzados;
